import React, { useContext } from "react";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  ActionButton,
  ActionLink,
  Icon,
  IconName,
  SanitizedHtml,
  useDeviceTypes,
} from "halifax";
import { useIsTravelApp } from "@capone/common";
import { AncillaryPostBookingPurchaseSuccess } from "redmond";
import { Redirect, RouteComponentProps } from "react-router-dom";
import { PATH_FLIGHTS } from "../../constants";
import { ClientContext } from "../../../../App";
import { pushToMyTrips } from "../../utils/queryStringHelpers";
import * as textConstants from "./constants";
import "./styles.scss";

interface ICfarPostBookingConfirmationProps
  extends Pick<RouteComponentProps, "history"> {
  purchaseResponse: AncillaryPostBookingPurchaseSuccess | null;
  confirmationEmail?: string;
}

export const CfarPostBookingConfirmation = ({
  purchaseResponse,
  confirmationEmail,
  history,
}: ICfarPostBookingConfirmationProps) => {
  const clientContext = useContext(ClientContext);
  const { sessionInfo } = clientContext;
  const { matchesMobile } = useDeviceTypes();
  const { isTravelApp } = useIsTravelApp();

  if (!purchaseResponse) {
    return <Redirect to={PATH_FLIGHTS} />;
  }

  const subtitleEarn = () => {
    const { rewardsPrice } = purchaseResponse;
    if (rewardsPrice) {
      return `${textConstants.SUBTITLE_EARN(rewardsPrice)}  `;
    }
    return "";
  };

  const subtitleEmail = () => {
    const emailFromResponse = purchaseResponse.emailAddress;
    const emailFromSession = sessionInfo?.userInfo?.email;
    return textConstants.SUBTITLE_EMAIL(
      emailFromResponse ?? emailFromSession ?? confirmationEmail ?? "",
    );
  };

  const navigate = () => {
    pushToMyTrips({ history, itineraryId: purchaseResponse.itineraryId });
  };

  const serviceExpiryDate = purchaseResponse.policyExpiration
    ? dayjs(purchaseResponse.policyExpiration).format("MMM DD")
    : "";

  const header = () => (
    <Box
      className={clsx("post-booking-offer-header-container", {
        isMobile: matchesMobile,
      })}
    >
      <Box
        className="logo-container"
        onClick={() => history.push({ pathname: PATH_FLIGHTS })}
      >
        {clientContext.logo}
      </Box>
    </Box>
  );

  return (
    <Box
      className={clsx("cfar-post-booking-root", {
        "cfar-post-booking-root-mobile": matchesMobile,
      })}
    >
      {!matchesMobile && header()}
      <Box
        className={clsx("post-booking-confirmation-root", {
          "pbc-mobile-root": matchesMobile,
        })}
      >
        <Icon className="checkmark" name={IconName.CheckCircleBorder} />
        <Typography variant="h1" className="title">
          {textConstants.TITLE}
        </Typography>
        <SanitizedHtml
          as={Typography}
          variant="h5"
          className="subtitle"
          html={subtitleEarn() + subtitleEmail()}
        />

        {matchesMobile ? (
          !isTravelApp && (
            <ActionButton
              defaultStyle="h4r-primary"
              className="view-details-button"
              onClick={navigate}
              message={textConstants.VIEW_TRIP_DETAILS}
            />
          )
        ) : (
          <>
            <ActionButton
              defaultStyle="h4r-primary"
              className="search-button"
              onClick={() => history.push(PATH_FLIGHTS)}
              message={textConstants.START_NEW_SEARCH}
            />
            <ActionLink
              className="view-details-link"
              content={
                <Typography variant="h4" className="view-details">
                  {textConstants.VIEW_TRIP_DETAILS}
                </Typography>
              }
              onClick={navigate}
            />
          </>
        )}

        <div className="confirmation-offer-divider" />

        <Typography variant="h4" className="whats-next">
          {textConstants.NEED_TO_CANCEL}
        </Typography>

        <Box className="step-item-container mt-20">
          <Box className="circle-bullet-container">
            <Typography variant="h3" className="circle-bullet">
              1
            </Typography>
          </Box>
          <Typography variant="h4" className="step-title">
            {textConstants.STEP_ONE}
          </Typography>
        </Box>

        <Box className="step-item-container mt-20">
          <Box className="circle-bullet-container">
            <Typography variant="h3" className="circle-bullet">
              2
            </Typography>
          </Box>
          <Typography variant="h4" className="step-title">
            {textConstants.STEP_TWO}
          </Typography>
        </Box>

        <Box className="step-item-container mt-20">
          <Box className="circle-bullet-container">
            <Typography variant="h3" className="circle-bullet">
              3
            </Typography>
          </Box>
          <Typography variant="h4" className="step-title">
            {textConstants.STEP_THREE}
          </Typography>
        </Box>

        <Box className="step-item-container">
          <Box className="circle-bullet-base" />
          <Typography variant="h4" className="step-body">
            {textConstants.STEP_THREE_BODY(serviceExpiryDate)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
